import { h, cloneElement, Component } from 'preact';
import style from './style';
import cx from 'classnames';
import { withAriaId } from '@cube/a11y';
import Icon from '../icon/index.js';
import ClickOutsideDetector from '../click-outside-detector';
import { KEY_CODES } from '../../constants/keycodes';
import dlv from 'dlv';

function getOptionId({ a11yId, index }) {
	return index !== -1 && `${a11yId}-${index}`;
}

/**
 * An auto-complete text input.
 *
 * 	- When not shown, show the auto-complete suggestions popover without highlighting any suggestions when:
 * 		- Suggestions exist for current value in the input text box AND ANY OF THE FOLLOWING:
 * 			- The user changes the contents of the input text box OR
 * 			- The user presses the DOWN or UP arrow
 * - When shown, hide the auto-complete suggestions popover when:
 * 		- The user hits the ESC key (this should also revert the text input to the last user entered value) OR
 *  	- The user focuses on something other than the input text box or suggestions pop-over (this should maintain)
 *	- When user modifies the text value by means other than highlighting a suggestion (e.g. typing, cut/paste, etc.)
 *		- onInput is fired with the current value showing in the input box AND
 *		- no suggestions are highlighted
 *  - When a user hits the DOWN arrow and the suggestions popover is open
 * 		- If no suggestion is highlighted, the first suggestion becomes "highlighted" and the text input shows the value of the highlighted item
 * 		- If a suggestion is highlighted other than the last suggestion, the next suggestion down becomes highlighted and the text input shows the value of the highlighted item
 * 		- If the last suggestion is highlighted, then no suggestion becomes highlighted and the text input shows the value that the user last entered
 *  - When a user hits the UP arrow and the suggestions popover is open
 * 		- If no suggestion is highlighted, the last suggestion becomes "highlighted" and the text input shows the value of the highlighted item
 * 		- If a suggestion is highlighted other than the first suggestion, the next suggestion up becomes highlighted and the text input shows the value of the highlighted item
 * 		- If the first suggestion is highlighted, then no suggestion becomes highlighted and the text input shows the value that the user last entered
 *  - When the user mouses over a suggestion, that suggestion is highlighted but the value of the text input does not change
 *  - When the user mouses out of the suggestion drop-down, then no suggestion is highlighted and the value of the text input does not change
 *  - When no suggestion is highlighted/highlighting is cleared
 * 		- What the user had personally entered last is shown (replacing any temporarily shown suggestion value if it was highlighted)
 *  - onInput event is fired when user modifies the text value by means other than highlighting a suggestion (e.g. typing, cut/paste, etc.) with the updated value shown in the text box
 *  - onChange event is fired when the user hits the ENTER key with the value currently showing in the input text box
 *
 */
export class InputTextBase extends Component {

	state = {
		highlightedSuggestion: -1,
		userEnteredValue: this.props.value,
		displayedValue: this.props.value,
		suggestionsActive: false
	}

	static defaultProps = {
		type: 'text'
	}

	setSuggestionsDimensions = () => {
		let target = this.props.suggestionsWidthTarget;
		if (typeof target === 'function') target = target();
		if (!target) return;

		let height, width, left, top, targetLeft, targetBottom;
		let { left: inputLeft, bottom: inputBottom } = this.input.getBoundingClientRect();

		// window has innerWidth, DOM elements need to use getBoundingClientRect
		if ((width=target.innerWidth)) {
			//Shift to the edge of the screen
			left = -inputLeft;
			height = target.innerHeight - inputBottom;
			top = inputBottom;
		}
		else {
			({ width, left: targetLeft, bottom: targetBottom } = target.getBoundingClientRect());
			//shift to the edge of the target element
			left = targetLeft - inputLeft;
			height = targetBottom - inputBottom;
			top = targetBottom;
		}
		this.setState({ height, left, width, top });
	}

	openSuggestions = () => {
		if (this.state.suggestionsActive) return;
		this.setSuggestionsDimensions();
		this.setState({ suggestionsActive: true });
	}

	closeSuggestions = () => this.setState({ suggestionsActive: false, highlightedSuggestion: -1 })

	/**
	 * Move the highlighted suggestion in the appropriate direction circularly by `inc` spaces, including the input text box as part of the circle
	 * of choices, and update the displayed value appropriately
	 * @param {Number} inc The number of spaces to move the highlighted item
	 */
	bumpHighlighted = (inc) => {
		let { children } = this.props;
		let highlightedSuggestion = this.state.highlightedSuggestion;
		highlightedSuggestion = (highlightedSuggestion + inc + 1) % ( children.length + 1 ) - 1;
		if (highlightedSuggestion === -2 )highlightedSuggestion = children.length-1;
		let displayedValue =  highlightedSuggestion !== -1 ? children[highlightedSuggestion].attributes.value : this.state.userEnteredValue;
		this.setState({ displayedValue, highlightedSuggestion, suggestionsActive: true });
	}

	/**
	 * Set the highlighted suggestion without updating the text input value
	 * @param {Number} highlightedSuggestion The index of the suggestion to highlight
	 */
	setHighlighted = (highlightedSuggestion) => {
		this.setState({ highlightedSuggestion });
	}

	clearHighlighted = () => this.setHighlighted(-1);

	handleSubmit = () => {
		this.closeSuggestions();
		this.props.onChange && this.props.onChange(this.state.displayedValue);
	}

	handleInput = (e) => {
		let value = e.target.value;
		this.setState({ userEnteredValue: value, displayedValue: value });
		this.openSuggestions();
		this.clearHighlighted();
		this.props.onInput && this.props.onInput(value);
	}

	handleClearInput = (e) => {
		this.input.focus();
		this.props.onClear && this.props.onClear(e, this.state.userEnteredValue);
		if (this.state.userEnteredValue !== '') {
			let value = '';
			this.setState({
				userEnteredValue: value,
				displayedValue: value,
				suggestionsActive: false,
				highlightedSuggestion: -1
			});
			this.props.onInput && this.props.onInput(value);
		}
	}

	handleKeyDown = (e) => {
		let { suggestionsActive, userEnteredValue } = this.state;

		// If using forceFocus, the suggestions are not active, and no value has been entered
		if (this.props.forceFocus && !suggestionsActive && userEnteredValue === '') {
			// If one of the predetermined key codes then remove focus
			if ([
				KEY_CODES.LEFT_ARROW, KEY_CODES.UP_ARROW, KEY_CODES.RIGHT_ARROW, KEY_CODES.DOWN_ARROW,
				KEY_CODES.SPACE_BAR,
				KEY_CODES.HOME, KEY_CODES.END
			].indexOf(e.keyCode) >= 0 ) {
				this.input.blur();
				this.setState({ focused: false });
				return;
			}
		}

		let preventDefault = true;
		if (e.keyCode === KEY_CODES.UP_ARROW) {
			suggestionsActive ? this.bumpHighlighted(-1) : this.openSuggestions();
		}
		else if (e.keyCode === KEY_CODES.DOWN_ARROW) {
			suggestionsActive ? this.bumpHighlighted(1) : this.openSuggestions();
		}
		else if (e.keyCode === KEY_CODES.CARRIAGE_RETURN) {
			this.handleSubmit();
		}
		else if (e.keyCode === KEY_CODES.ESCAPE) {
			this.setState({ displayedValue: userEnteredValue });
			this.closeSuggestions();
		}
		else {
			preventDefault = false;
		}
		preventDefault && e.preventDefault();
	}

	handleFocus = () => {
		if (this.props.showOverlay) {
			this.setSuggestionsDimensions();
		}
		if (this.props.onFocus) {
			this.props.onFocus();
		}
		this.setState({ focused: true });
	}

	handleClickOutside = (e) => {
		//ignore callbacks triggered because of Escape key handler in ClickOutsideDetector
		if (e && e.keyCode !== KEY_CODES.ESCAPE) {
			this.closeSuggestions();
			this.setState({ focused: false });
		}
	}

	handleBlur = (e) => {
		if (this.props.onBlur) {
			this.props.onBlur();
		}
		//close suggestions if we are blurred because something else on this page took focus (as opposed to just the whole window losing focus);
		if (e.relatedTarget) {
			this.closeSuggestions();
			this.setState({ focused: false });
		}
	}

	handleOptionClick = ({ value }) => {
		this.setState({ userEnteredValue: value, displayedValue: value, highlightedSuggestion: -1 });
		this.handleSubmit();
	}

	componentDidMount() {
		if (this.props.forceFocus && this.input) {
			this.input.focus();
		}
	}

	render({ valueTextKey, children, disabled, a11yId, inputClass, clearTextClass, listClass, class: divClass, suggestionsFooter, onChange, showOverlay, type, onFocus, onBlur, clearTextIcon, showClear, ...props },
		{ focused, displayedValue, highlightedSuggestion, suggestionsActive, height, width, left, top }) {
		//It is assumed all children are Option elements
		children = children.map((child, index) => (
			cloneElement(child, {
				selected: highlightedSuggestion === index,
				focused: highlightedSuggestion === index,
				icon: false,  //modify icon prop to be false instead of default 'ok' icon for text input suggestions
				iconPosition: false, // modify icon position to be false instead of default 'left' for text input suggestions
				index,
				a11yId,
				onMouseOver: this.setHighlighted,
				onChange: this.handleOptionClick
			})
		));

		let ulID = `${a11yId}-ul`;

		let showSuggestions = !!children.length && suggestionsActive;

		let suggestionsStyle = (left || width) && { style: `left: ${left}px; top: ${top}px; height: ${height}px; width: ${width}px;` };

		return (
			<ClickOutsideDetector onClickOutside={this.handleClickOutside}>
				<div
					class={cx(style.wrapper, divClass, disabled && style.disabled)}
				>
					<input
						type={type}
						ref={input => this.input = input}
						aria-disabled={disabled && 'true'}
						aria-expanded={showSuggestions && 'true'}
						aria-activedescendant={getOptionId({ a11yId, index: highlightedSuggestion })}
						aria-owns={showSuggestions && ulID}
						aria-autocomplete="list"
						role="combobox"
						tabindex={!disabled && '0'}
						disabled={disabled}
						{...props}
						value={typeof displayedValue === 'string' ? displayedValue : dlv(displayedValue, valueTextKey || 'title')}
						qevalue={typeof displayedValue === 'string' ? displayedValue : dlv(displayedValue, valueTextKey || 'title')}
						onBlur={this.handleBlur}
						onFocus={this.handleFocus}
						onInput={this.handleInput}
						onKeyDown={this.handleKeyDown}
						class={cx(style.textInput, inputClass)}
					/>

					{
						focused && showClear &&
						<Icon
							class={cx(style.clear, clearTextClass)}
							onClick={this.handleClearInput}
							name={clearTextIcon ? clearTextIcon : 'close-cube'}
						/>
					}

					{
						showOverlay && focused &&
						<div class={style.overlay} {...suggestionsStyle} />
					}
					{showSuggestions &&
						<div class={cx(style.suggestions, showOverlay && style.overlay, listClass)} {...suggestionsStyle} >
							<ul
								id={ulID}
								role="listbox"
								onMouseOut={this.clearHighlighted}
							>
								{children}
								{suggestionsFooter &&
									<li role="presentation" class={style.item}>
										{suggestionsFooter}
									</li>
								}
							</ul>
						</div>
					}
				</div>
			</ClickOutsideDetector>
		);
	}
}
export const InputText = withAriaId('input-text')(InputTextBase);
